import React from "react";
import { Box, Divider } from "@mui/material";
import { dt, accentShimmer } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Offer } from "../../models/models";
import { getUiLocale } from "../../../../shared/utils/relativeTime";

interface OfferPricingCardProps {
  offer: Partial<Offer>;
}

const formatCurrency = (value?: number): string => {
  if (value === undefined || value === null) return "\u2014";
  return new Intl.NumberFormat(getUiLocale(), {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PricingRow: React.FC<{ label: string; value: React.ReactNode; bold?: boolean }> = ({ label, value, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: "8px" }}>
    <Box
      sx={{
        fontSize: bold ? "14px" : "13px",
        fontWeight: bold ? 700 : 500,
        fontFamily: dt.font,
        color: bold ? dt.textPrimary : dt.textSecondary,
        flex: "0 0 45%",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: bold ? "16px" : "13px",
        fontWeight: bold ? 700 : 600,
        fontFamily: dt.fontMono,
        color: bold ? dt.blue : dt.textPrimary,
        textAlign: "right",
        flex: "0 0 55%",
      }}
    >
      {value}
    </Box>
  </Box>
);

const OfferPricingCard: React.FC<OfferPricingCardProps> = ({ offer }) => {
  const { translate } = useTranslation(["serviceOffers"]);

  const totalCost = (offer.laborCost || 0) + (offer.partsCost || 0) + (offer.travelCost || 0) + (offer.additionalCosts || 0);

  const travelValueContent = (
    <Box component="span">
      {formatCurrency(offer.travelCost)}
      {(offer.travelDistanceKm != null || offer.travelTimeHours != null) && (
        <Box component="span" sx={{ ml: 0.5, fontSize: "11px", color: dt.textMuted }}>
          ({[
            offer.travelDistanceKm != null ? `${offer.travelDistanceKm} km` : null,
            offer.travelTimeHours != null ? `${offer.travelTimeHours}h` : null,
          ].filter(Boolean).join(", ")})
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: dt.radius,
        overflow: "hidden",
      }}
    >
      {/* Gradient accent bar */}
      <Box sx={{ height: "3px", background: accentShimmer }} />

      <Box sx={{ padding: "20px" }}>
        {/* Section label */}
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
            color: dt.textMuted,
            fontFamily: dt.font,
            mb: "12px",
          }}
        >
          {translate("pricingDetails")}
        </Box>

        <PricingRow label={translate("laborCost")} value={formatCurrency(offer.laborCost)} />
        {offer.laborHours != null && (
          <PricingRow label={translate("laborHours")} value={`${offer.laborHours} hrs`} />
        )}
        <PricingRow label={translate("partsCost")} value={formatCurrency(offer.partsCost)} />
        <PricingRow label={translate("travelCost")} value={travelValueContent} />
        <PricingRow label={translate("additionalCosts")} value={formatCurrency(offer.additionalCosts)} />

        <Divider sx={{ my: 1 }} />

        <PricingRow
          label={translate("totalEstimatedCost")}
          value={formatCurrency(totalCost)}
          bold
        />
      </Box>
    </Box>
  );
};

export default OfferPricingCard;
